import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FullscreenSpinner,
  EmptyState,
} from '@shared/ui';
import { manufacturerApi } from '@entities/manufacturer';
import { MediaImage, MediaVideo } from '@entities/media';
import { ROUTES } from '@shared/config/routes';
import { KycActionsPanel } from '@features/manufacturer-kyc';
import { EditProfileDialog, SetPasswordDialog } from '@features/manufacturer-onboarding';
import { CertificateCard } from './certificate-card';

export function ManufacturerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [editOpen, setEditOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['manufacturer', id],
    queryFn: () => manufacturerApi.get(id),
    enabled: !!id,
  });

  if (isLoading) return <FullscreenSpinner />;
  if (isError || !data) return <EmptyState title="Не найдено" />;

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.companyName}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <span>{data.country}</span>
            {data.ecoProducer ? <Badge variant="success">eco</Badge> : null}
            {data.active ? <Badge variant="secondary">active</Badge> : <Badge variant="destructive">inactive</Badge>}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Профиль</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {data.logo ? (
              <MediaImage
                link={data.logo}
                alt={data.companyName}
                className="h-20 w-20 rounded-md border object-cover"
              />
            ) : null}
            {data.description ? <p>{data.description}</p> : null}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {data.website ? (
                <>
                  <dt className="text-muted-foreground">Website</dt>
                  <dd>
                    <a href={data.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                      {data.website}
                    </a>
                  </dd>
                </>
              ) : null}
              {data.foundedYear ? (
                <>
                  <dt className="text-muted-foreground">Основана</dt>
                  <dd>{data.foundedYear}</dd>
                </>
              ) : null}
              {data.employeesRange ? (
                <>
                  <dt className="text-muted-foreground">Сотрудники</dt>
                  <dd>{data.employeesRange}</dd>
                </>
              ) : null}
            </dl>
          </CardContent>
        </Card>

        {data.video ? (
          <Card>
            <CardHeader>
              <CardTitle>Видео</CardTitle>
            </CardHeader>
            <CardContent>
              <MediaVideo link={data.video} className="w-full rounded" />
            </CardContent>
          </Card>
        ) : null}

        {data.certificates.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Сертификаты</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.certificates.map((link) => (
                  <CertificateCard key={link.id} link={link} />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {data.ecoCertificates.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Эко-сертификаты</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.ecoCertificates.map((link) => (
                  <CertificateCard key={link.id} link={link} />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Действия</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              Редактировать профиль
            </Button>
            <Button variant="outline" onClick={() => setPwOpen(true)}>
              Выдать новый пароль
            </Button>
          </CardContent>
        </Card>

        <KycActionsPanel profile={data} />

        <Card>
          <CardHeader>
            <CardTitle>Связанные</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Link to={`${ROUTES.catalog}?manufacturer=${data.id}`} className="block text-primary hover:underline">
              Каталог производителя
            </Link>
            <Link
              to={`${ROUTES.catalog}?manufacturer=${data.id}&status=PENDING_MODERATION`}
              className="block text-primary hover:underline"
            >
              Товары на модерации
            </Link>
          </CardContent>
        </Card>
      </div>

      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} profile={data} />
      <SetPasswordDialog
        open={pwOpen}
        onOpenChange={setPwOpen}
        manufacturerId={data.id}
        email={null}
      />
    </div>
  );
}
